<script setup lang="ts">
import { computed, onMounted, onUnmounted, useTemplateRef } from "vue";
import {
  useAnchoredFloating,
  type AnchorSource,
} from "../../composables/useAnchoredFloating";
import { nothing, toMaybe } from "../../lib/maybe";
import { contextMenuState } from "../../lib/platform/contextMenu";

const panel = useTemplateRef<HTMLElement>("panel");

const anchor = computed<AnchorSource>(() =>
  contextMenuState.open ? toMaybe(contextMenuState.anchor) : nothing,
);

const open = computed(() => contextMenuState.open);
const panelMaybe = computed(() => toMaybe(panel.value));

const { floatingStyle, side } = useAnchoredFloating(anchor, panelMaybe, open, {
  placement: () => "bottom-start",
  gap: 2,
});

function close(): void {
  contextMenuState.open = false;
  contextMenuState.items = [];
}

function onSelect(item: (typeof contextMenuState.items)[number]): void {
  if ("type" in item) return;
  if (item.disabled === true) return;
  close();
  void item.action();
}

function onDocPointer(event: PointerEvent): void {
  if (contextMenuState.open === false) return;
  const target = event.target;
  const panelEl = panel.value;
  if (
    target instanceof Node &&
    panelEl !== null &&
    panelEl.contains(target) === true
  ) {
    return;
  }
  close();
}

function onKeydown(event: KeyboardEvent): void {
  if (contextMenuState.open === false || event.key !== "Escape") return;
  event.preventDefault();
  close();
}

onMounted(() => {
  window.addEventListener("pointerdown", onDocPointer, true);
  window.addEventListener("keydown", onKeydown);
});

onUnmounted(() => {
  window.removeEventListener("pointerdown", onDocPointer, true);
  window.removeEventListener("keydown", onKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="contextMenuState.open"
      ref="panel"
      class="zw-menu-panel"
      :data-side="side"
      :style="floatingStyle"
      role="menu"
      data-context-menu
    >
      <template v-for="(item, i) in contextMenuState.items" :key="i">
        <div
          v-if="'type' in item && item.type === 'separator'"
          class="zw-menu-sep"
          role="separator"
          aria-hidden="true"
        />
        <button
          v-else-if="'id' in item"
          type="button"
          class="zw-menu-item"
          role="menuitem"
          :disabled="item.disabled"
          @click="onSelect(item)"
        >
          <span class="zw-menu-item-main">
            <span class="zw-menu-item-label">{{ item.label }}</span>
          </span>
        </button>
      </template>
    </div>
  </Teleport>
</template>
